import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cx } from '../cx.js';

export type VarianteButton = 'primary' | 'secondary' | 'discreet';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: VarianteButton;
  /** Mostra uno spinner e il testo di attesa, disabilitando il bottone. */
  loading?: boolean;
  loadingText?: string;
  icon?: LucideIcon;
  /** Bottone grande: icona 24px e padding maggiore. */
  size?: 'md' | 'lg';
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 min-h-[44px] rounded-md t-label px-6 py-3 ' +
  'transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed select-none';

const varianti: Record<VarianteButton, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-strong',
  secondary: 'bg-surface text-ink border border-border-strong hover:bg-surface-sunken',
  discreet: 'bg-transparent text-accent hover:text-accent-strong hover:underline px-3',
};

/**
 * Bottone del design system. Regola del brand book: un solo `variant="primary"` per schermata.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', loading = false, loadingText, icon: Icon, size = 'md', fullWidth, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  const dimensioneIcona = size === 'lg' ? 24 : 20;
  return (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cx(base, varianti[variant], size === 'lg' && 'min-h-[52px] text-base', fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? (
        <Loader2 aria-hidden size={dimensioneIcona} strokeWidth={1.75} className="animate-spin" />
      ) : (
        Icon && <Icon aria-hidden size={dimensioneIcona} strokeWidth={1.75} />
      )}
      <span>{loading && loadingText ? loadingText : children}</span>
    </button>
  );
});
